/**
 * useMyProgress
 *
 * Fetches calendar-bounded user progress aggregates for the Progress page.
 * Honors VITE_DATA_MODE: returns mock or empty data when the page is
 * being exercised without a live backend.
 *
 * Backend endpoint: GET /analytics/me/progress?period=...&year=YYYY
 */

import { useQuery } from '@tanstack/react-query';
import {
  progressApi,
  type MyProgressResponse,
  type ProgressPeriod,
} from '@/lib/api-client';
import { useDataMode } from '@/contexts/DataModeContext';
import { loadMockProgressData } from '@/data';

// Brief delay in mock mode so React Query's `isLoading` flashes the
// skeletons once on mount and on period switches - long enough to see
// the loading state, short enough not to feel laggy.
const MOCK_DELAY_MS = 300;

export function useMyProgress(period: ProgressPeriod, year?: number) {
  const { isMock, isEmpty } = useDataMode();

  return useQuery<MyProgressResponse>({
    queryKey: ['my-progress', period, year ?? 'current', isMock, isEmpty],
    queryFn: async () => {
      if (isEmpty)
        return (await loadMockProgressData()).buildEmptyProgress(period, year);
      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, MOCK_DELAY_MS));
        return (await loadMockProgressData()).buildMockProgress(period, year);
      }
      return progressApi.getMyProgress(period, year);
    },
    staleTime: 60_000,
  });
}
